import React, { useCallback, useEffect, useMemo, useState } from "react";

import { CurrentLineConfig } from "components/dashboard/dataset-window/DatasetLineConfigOptions";
import DatasetView from "components/dashboard/dataset-window/DatasetView";
import { Dataset } from "components/dashboard/dataset-window/models/types";
import { removeItemById } from "components/dashboard/dataset-window/utils/utils";
import { DatasetInfo, Maybe } from "gql/generated/types";
import { ListBox } from "primereact/listbox";
import { T } from "services/lang/LanguageService";
import { measurementOrder } from "../utils/const";
import { measurementUnits } from "../utils/utils";

type DatasetsTabProps = {
  datasets: Maybe<DatasetInfo>[] | undefined;
}

const DatasetsTab: React.FC<DatasetsTabProps> = ({ datasets }) => {
  const formattedData: Dataset[] = useMemo(
    () =>
      [...(datasets ?? [])]
        ?.sort((a, b) => {
          const aTitle =
            a?.title?.split(":").filter(i => measurementOrder.includes(i))[0] ?? a?.title ?? "";
          const bTitle =
            b?.title?.split(":").filter(i => measurementOrder.includes(i))[0] ?? b?.title ?? "";
          const indexA = measurementOrder.indexOf(aTitle);
          const indexB = measurementOrder.indexOf(bTitle);
          if (indexA === -1 && indexB === -1) return aTitle.localeCompare(bTitle);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        })
        ?.map(dataset => ({
          identifier: dataset?.identifier ?? "",
          formatedTitle: dataset?.title ?? "",
          group: dataset?.groupTitle ?? "",
          title: dataset?.title ?? "",
          unit: measurementUnits(dataset?.title?.split(":")?.filter(i => measurementOrder.includes(i))[0] ?? "") ?? ""
        })) ?? [],
    [datasets]
  );
  
  const [selectedDatasets, setSelectedDatasets] = useState<Dataset[]>(formattedData.slice(0, 15));

  useEffect(() => {
    setSelectedDatasets(formattedData.slice(0, 15))
  }, [formattedData])
  
  const removeDataset = useCallback(
    (removedId: string | string[]) => {
      if (typeof removedId === "string") {
        setSelectedDatasets(removeItemById(selectedDatasets, removedId));
      }
      if (Array.isArray(removedId)) {
        setSelectedDatasets(prev =>
          prev.filter(dataset => !removedId.includes(dataset.identifier))
        );
      }
    },
    [selectedDatasets]
  );

  if (!datasets || datasets?.length === 0) return <span className="mt-3 ml-3">{T("mdm.no-datasets-are-linked")}</span>;

  const setCurrentLineConfig = (identifier: string, currentLineConfig: CurrentLineConfig) => {
    const newSelectedDatasets = selectedDatasets.map(dataset => {
      if (dataset.identifier === identifier) {
        return { ...dataset, currentLineConfig };
      }
      return dataset;
    });
    setSelectedDatasets(newSelectedDatasets);
  };

  return (
    <div className="w-full mt-3 flex" style={{ minHeight: "700px" }}>
      <ListBox
        value={selectedDatasets}
        options={formattedData ?? []}
        onChange={e => setSelectedDatasets(e.value)}
        placeholder={T("word.datasets")}
        optionLabel="title"
        className="p-inputtext-sm m-2"
        multiple
        filter
      />
      <DatasetView
        selectedDatasets={selectedDatasets}
        removeDataset={removeDataset}
        setSelectedDatasets={setSelectedDatasets}
        initialChart="RAW"
        initialTimeOffset="DAYS"
        setCurrentLineConfig={setCurrentLineConfig}
      />
    </div>
  );
}

export default DatasetsTab;